import { Cake, TreePine } from "lucide-react";
import ScoopBall from "@/components/ScoopBall";
import { FLAVORS_GLACES, FLAVORS_SORBETS } from "@/data/flavors";

function FlavorCard({ flavor, index }) {
    return (
        <li
            data-testid={`flavor-${flavor.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="group flex flex-col items-center text-center gap-4 p-5 rounded-2xl bg-white/60 hover:bg-white border border-transparent hover:border-arl-blue/10 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
            style={{ animationDelay: `${index * 40}ms` }}
        >
            <div className="transition-transform group-hover:scale-105 group-hover:rotate-[-3deg]">
                <ScoopBall img={flavor.img} colors={flavor.colors} alt={`Boule ${flavor.name}`} />
            </div>
            <p className="font-display text-lg sm:text-xl text-arl-ink leading-tight">{flavor.name}</p>
        </li>
    );
}

function FlavorSection({ eyebrow, title, accent, items, testid }) {
    return (
        <section data-testid={testid} className="relative">
            <div className="flex items-baseline gap-4 mb-2">
                <span
                    className={`w-2.5 h-2.5 rounded-full ${accent} inline-block`}
                    aria-hidden="true"
                />
                <p className="text-xs uppercase tracking-[0.24em] text-arl-ink/55 font-semibold">
                    {eyebrow}
                </p>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-arl-ink leading-tight mb-10">
                {title}
            </h2>
            <ul
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4"
                data-testid={`${testid}-list`}
            >
                {items.map((flavor, idx) => (
                    <FlavorCard key={flavor.name} flavor={flavor} index={idx} />
                ))}
            </ul>
        </section>
    );
}

export default function Parfums() {
    return (
        <div data-testid="parfums-page">
            {/* HERO + PRICING */}
            <section className="relative overflow-hidden pt-12 pb-16 sm:pt-16 sm:pb-20">
                <div className="absolute inset-0 rhombus-bg opacity-60 pointer-events-none" />

                <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-end">
                    <div className="animate-fade-up">
                        <h1
                            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-arl-ink"
                            data-testid="parfums-title"
                        >
                            Nos parfums,
                            <br />
                            <span className="font-script text-arl-blue text-6xl sm:text-7xl lg:text-[5rem] inline-block leading-none">
                                au gré des saisons.
                            </span>
                        </h1>
                        <p className="mt-6 text-lg text-arl-ink/75 max-w-xl leading-relaxed">
                            Crèmes glacées et sorbets plein fruit, faits maison à Bourg-en-Bresse. La carte
                            évolue régulièrement, voici les classiques que vous trouverez en boutique.
                        </p>
                    </div>

                    {/* Pricing card */}
                    <div
                        className="relative rounded-[1.8rem] bg-arl-blue text-arl-cream p-7 sm:p-8 shadow-xl"
                        data-testid="parfums-pricing"
                    >
                        <div className="absolute inset-0 opacity-20 rhombus-bg rounded-[1.8rem] pointer-events-none" />
                        <p className="relative text-xs uppercase tracking-[0.22em] text-arl-sun font-semibold">
                            Tarifs en boutique
                        </p>
                        <p className="relative mt-2 font-display text-2xl">À la boule, à composer.</p>
                        <ul className="relative mt-6 grid grid-cols-3 gap-3 text-center" data-testid="pricing-tiers">
                            {[
                                { qty: "1 boule", price: "3 €" },
                                { qty: "2 boules", price: "5 €" },
                                { qty: "3 boules", price: "7 €" },
                            ].map((p) => (
                                <li
                                    key={p.qty}
                                    className="rounded-2xl bg-arl-cream/10 backdrop-blur p-4 border border-arl-cream/15"
                                >
                                    <p className="text-xs uppercase tracking-wide text-arl-cream/70">
                                        {p.qty}
                                    </p>
                                    <p className="mt-1 font-display text-3xl text-arl-sun">{p.price}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* GLACES */}
            <section className="relative pb-16 sm:pb-20">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <FlavorSection
                        testid="section-glaces"
                        eyebrow="Crèmes glacées"
                        title="Les glaces"
                        accent="bg-arl-rose"
                        items={FLAVORS_GLACES}
                    />
                </div>
            </section>

            {/* SORBETS */}
            <section className="relative py-16 sm:py-20 bg-arl-paper">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <FlavorSection
                        testid="section-sorbets"
                        eyebrow="Plein fruit"
                        title="Les sorbets"
                        accent="bg-arl-orange"
                        items={FLAVORS_SORBETS}
                    />
                </div>
            </section>

            {/* POTS 500 ML */}
            <section
                className="relative py-20 sm:py-28"
                data-testid="section-pots"
            >
                <div className="mx-auto max-w-6xl px-5 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                        <div className="lg:col-span-5">
                            <div className="relative max-w-[360px] mx-auto">
                                <div className="absolute -inset-3 bg-arl-sun/15 rounded-[1.8rem] rotate-[2deg]" />
                                <img
                                    src="/images/pots.png"
                                    alt="Pots de 500 ml de glaces Arlequin dans le frigo"
                                    className="relative w-full aspect-[4/5] object-cover rounded-[1.5rem] shadow-xl border border-white"
                                    data-testid="pots-image"
                                />
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.24em] text-arl-blue/70 font-semibold">
                                À emporter à la maison
                            </p>
                            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-arl-ink leading-tight">
                                Les pots de 500 ml,
                                <span className="font-script text-arl-blue"> pour la maison.</span>
                            </h2>
                            <p className="mt-5 text-arl-ink/75 leading-relaxed">
                                On vous prépare vos parfums préférés en pots de 500 ml, à emporter et
                                garder au congélateur. Demandez la disponibilité du jour en boutique.
                            </p>

                            <div
                                className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-6 sm:gap-10"
                                data-testid="pots-pricing"
                            >
                                <div className="inline-flex items-baseline gap-3">
                                    <span className="font-display text-5xl text-arl-blue leading-none">
                                        9 €
                                    </span>
                                    <span className="text-sm text-arl-ink/55">
                                        le pot de 500 ml
                                    </span>
                                </div>

                                <div className="hidden sm:block w-px h-20 bg-arl-blue/15" aria-hidden="true" />

                                <div>
                                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-arl-rose mb-3">
                                        Carte fidélité
                                    </p>
                                    <div className="flex items-center gap-2">
                                        {Array.from({ length: 10 }).map((_, i) => (
                                            <span
                                                key={i}
                                                aria-hidden="true"
                                                className="w-3.5 h-3.5 rotate-45 border-[1.5px] border-arl-blue/35 bg-arl-blue/5"
                                            />
                                        ))}
                                        <span
                                            aria-hidden="true"
                                            className="ml-2 w-5 h-5 rotate-45 bg-arl-rose shadow-[0_4px_10px_-2px_rgba(232,77,122,0.45)]"
                                        />
                                    </div>
                                    <p className="mt-3 text-sm text-arl-ink/75 leading-snug">
                                        10 pots achetés, le 11<sup>e</sup> offert.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* GATEAUX GLACES + BUCHES */}
            <section
                className="relative pt-12 sm:pt-16 pb-24"
                data-testid="section-cakes"
            >
                <div className="mx-auto max-w-6xl px-5 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                        <div className="lg:col-span-6 order-2 lg:order-1">
                            <p className="text-xs uppercase tracking-[0.24em] text-arl-blue/70 font-semibold">
                                Sur commande
                            </p>
                            <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-tight text-arl-ink">
                                Gâteaux glacés
                                <span className="font-script text-arl-blue"> & bûches de Noël.</span>
                            </h2>
                            <p className="mt-5 text-arl-ink/75 leading-relaxed">
                                On fait aussi des gâteaux glacés à composer toute l'année, et à
                                l'approche de Noël, des bûches glacées sur commande. Parlons-en
                                directement en boutique pour le parfum, la taille et la date qui vous
                                arrangent.
                            </p>

                            <div
                                className="mt-7 grid sm:grid-cols-2 gap-3"
                                data-testid="cakes-tiles"
                            >
                                <div className="rounded-2xl bg-white p-4 border border-arl-blue/10 flex items-center gap-3 hover:border-arl-blue/30 transition">
                                    <span className="w-10 h-10 rounded-xl bg-arl-sun/30 text-arl-orange flex items-center justify-center shrink-0">
                                        <Cake size={18} />
                                    </span>
                                    <div>
                                        <p className="font-semibold text-arl-ink text-sm">
                                            Gâteaux glacés
                                        </p>
                                        <p className="text-xs text-arl-ink/60">
                                            Vacherins, entremets, omelettes norvégiennes
                                        </p>
                                    </div>
                                </div>
                                <div className="rounded-2xl bg-white p-4 border border-arl-blue/10 flex items-center gap-3 hover:border-arl-blue/30 transition">
                                    <span className="w-10 h-10 rounded-xl bg-arl-rose/15 text-arl-rose flex items-center justify-center shrink-0">
                                        <TreePine size={18} />
                                    </span>
                                    <div>
                                        <p className="font-semibold text-arl-ink text-sm">
                                            Bûches de Noël
                                        </p>
                                        <p className="text-xs text-arl-ink/60">
                                            Sur commande pendant la saison
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-6 order-1 lg:order-2">
                            <div className="relative">
                                <div className="absolute -inset-4 bg-arl-rose/10 rounded-[2.2rem] rotate-[-2deg]" />
                                <img
                                    src="/images/gateaux-glaces.webp"
                                    alt="Gâteau glacé fruits rouges et meringue chez Arlequin"
                                    className="relative w-full aspect-[4/3] object-cover rounded-[1.8rem] shadow-xl"
                                    data-testid="buche-image"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
